import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { StarRating } from "./StarRating";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { sanitizeText, sanitizeName } from "@/lib/sanitize";

interface EditReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  review: {
    id: string;
    rating: number;
    title: string;
    comment: string;
    pros?: string | null;
    cons?: string | null;
    service_date?: string | null;
  } | null;
  professionalName?: string;
  onSuccess?: () => void;
}

export const EditReviewModal = ({
  isOpen,
  onClose,
  review,
  professionalName,
  onSuccess,
}: EditReviewModalProps) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [pros, setPros] = useState("");
  const [cons, setCons] = useState("");
  const [serviceDate, setServiceDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (review) {
      setRating(review.rating);
      setTitle(review.title);
      setComment(review.comment);
      setPros(review.pros || "");
      setCons(review.cons || "");
      setServiceDate(review.service_date || "");
    }
  }, [review]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!review) return;

    if (rating === 0) {
      toast.error(t("reviews.ratingRequired"));
      return;
    }

    if (comment.length < 50) {
      toast.error(t("reviews.commentTooShort"));
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from("reviews")
        .update({
          rating,
          title: sanitizeName(title),
          comment: sanitizeText(comment),
          pros: pros ? sanitizeText(pros) : null,
          cons: cons ? sanitizeText(cons) : null,
          service_date: serviceDate || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", review.id);

      if (error) throw error;

      toast.success(t("reviews.updateSuccess", "Recensione aggiornata"));
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error("Error updating review:", error);
      toast.error(t("reviews.updateError", "Errore durante l'aggiornamento"));
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {t("reviews.editReview", "Modifica recensione")}{professionalName ? ` - ${professionalName}` : ''}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Rating */}
          <div>
            <label className="font-bold mb-2 block">
              {t("reviews.yourRating")} *
            </label>
            <StarRating
              rating={rating}
              onChange={setRating}
              size="lg"
              interactive
            />
          </div>

          {/* Service Date */}
          <div>
            <label className="font-bold mb-2 block">
              {t("reviews.serviceDate")}
            </label>
            <BrutalInput
              type="date"
              max={today}
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
            />
          </div>

          {/* Title */}
          <div>
            <label className="font-bold mb-2 block">
              {t("reviews.reviewTitle")} *
            </label>
            <BrutalInput
              type="text"
              maxLength={200}
              required
              placeholder={t("reviews.titlePlaceholder")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Comment */}
          <div>
            <label className="font-bold mb-2 block">
              {t("reviews.yourExperience")} * ({t("reviews.minimum50Chars")})
            </label>
            <textarea
              className="w-full min-h-32 px-4 py-3 border-[3px] border-dark bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              required
              minLength={50}
              placeholder={t("reviews.commentPlaceholder")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <div className="text-sm text-muted-foreground mt-1">
              {comment.length}/50 {t("reviews.charactersMinimum")}
            </div>
          </div>

          {/* Pros and Cons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-bold mb-2 block">{t("reviews.pros")}</label>
              <textarea
                className="w-full h-20 px-4 py-3 border-[3px] border-dark bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={t("reviews.prosPlaceholder")}
                value={pros}
                onChange={(e) => setPros(e.target.value)}
              />
            </div>
            <div>
              <label className="font-bold mb-2 block">{t("reviews.cons")}</label>
              <textarea
                className="w-full h-20 px-4 py-3 border-[3px] border-dark bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={t("reviews.consPlaceholder")}
                value={cons}
                onChange={(e) => setCons(e.target.value)}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <BrutalButton type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" disabled={submitting}>
              {submitting ? t("common.submitting") : t("reviews.saveChanges", "Salva modifiche")}
            </BrutalButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
